import { useMemo } from 'react';

import config from 'config';

import useBatchedGqlProductPricing from './useBatchedGqlProductPricing';
import useBatchedGqlRelatedProducts from './useBatchedGqlRelatedProducts';
import useCioBrowseFacets from './useCioBrowseFacets';
import useCioBrowseProducts from './useCioBrowseProducts';
import useFacetTracking from './useFacetTracking';

const useCioBrowseAndGqlProducts = () => {
  const { selectedUrlFilters, allCioFacets, allCioFacetNames } =
    useCioBrowseFacets();

  const {
    data: cioBrowseResults,
    isLoading: isCioBrowseLoading,
    isFetching: isCioBrowseFetching,
    // error,
    fetchNextPage,
    hasNextPage,
    cioFilterName,
    cioFilterValue,
    selectedSortOption,
  } = useCioBrowseProducts({ selectedUrlFilters, allCioFacets });

  const masterProductIds = useMemo(() => {
    return cioBrowseResults?.pages
      ?.flatMap((page) => page?.response?.results)
      ?.map((result) => result?.data.id);
  }, [cioBrowseResults]);

  const {
    data: pricingData,
    isLoading: isProductPricingLoading,
    isFetching: isProductPricingFetching,
  } = useBatchedGqlProductPricing(masterProductIds);
  const {
    data: relatedProductsData,
    isLoading: isRelatedProductsLoading,
    isFetching: isRelatedProductsFetching,
  } = useBatchedGqlRelatedProducts(masterProductIds);

  // Combine the cioBrowseResults with the pricingData and relatedProductsData
  const products = useMemo(
    () =>
      cioBrowseResults?.pages
        ?.flatMap((page) => page.response?.results)
        ?.map((result) => ({
          ...result?.data,
          pricing: pricingData?.find(
            (pricing) =>
              Number(pricing.masterProductId) === Number(result?.data.id)
          ),
          relatedProductsData: relatedProductsData?.find(
            (relatedProducts) =>
              Number(relatedProducts.masterProductId) ===
              Number(result?.data.id)
          ),
        })),
    [cioBrowseResults, pricingData, relatedProductsData]
  );

  const totalNumResults = cioBrowseResults?.pages?.[0]?.totalNumResults;

  const facetBlacklist = config.get('public.brand.constructor.facetBlacklist');
  const cioFacets = useMemo(() => {
    const rawCioFacets = cioBrowseResults?.pages?.[0]?.response?.facets;
    return rawCioFacets?.filter(
      (facet) => !facetBlacklist.includes(facet.name)
    );
  }, [cioBrowseResults, facetBlacklist]);

  const cioSortOptions = useMemo(() => {
    return cioBrowseResults?.pages?.[0]?.response?.sort_options;
  }, [cioBrowseResults]);

  useFacetTracking({ selectedUrlFilters, selectedSortOption });

  return {
    products,
    fetchNextPage,
    hasNextPage,
    cioFacets,
    cioSortOptions,
    selectedUrlFilters,
    allCioFacets,
    allCioFacetNames,
    totalNumResults,
    isCioBrowseLoading,
    isCioBrowseFetching,
    isRelatedProductsLoading,
    isRelatedProductsFetching,
    isProductPricingLoading,
    isProductPricingFetching,
    cioFilterName,
    cioFilterValue,
  };
};

export default useCioBrowseAndGqlProducts;
